import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Trash2, MoreHorizontal, Package, Truck, AlertTriangle } from 'lucide-react';
import { format } from 'date-fns';
import { useSecureMutation } from '@/hooks/useSecureMutation';
import { toast } from 'sonner';

export const OrdersList = () => {
  const [approvalWarningOrder, setApprovalWarningOrder] = useState<any>(null);
  const queryClient = useQueryClient();
  
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          customer:customer_id(company_name)
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const updateOrderStatusMutation = useSecureMutation(
    async ({ orderId, newStatus }: { orderId: string; newStatus: string }) => {
      // Backend triggers will handle all inventory management automatically
      // Just update the order status
      const { error: statusError } = await supabase
        .from('orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (statusError) throw statusError;

      return { orderId, newStatus };
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['orders'] });
        queryClient.invalidateQueries({ queryKey: ['inventory-items'] });
        queryClient.invalidateQueries({ queryKey: ['inventory-transactions'] });
        toast.success('Order status updated successfully');
        setApprovalWarningOrder(null);
      },
      onError: (error: any) => {
        toast.error(`Failed to update order status: ${error.message || 'Unknown error'}`);
        console.error('Error updating order status:', error);
      },
    }
  );

  const handleStatusChange = (order: any, newStatus: string) => {
    // Check if trying to approve an order with modified invoice
    if (newStatus === 'confirmed' && order.status === 'unapproved' && order.invoice_modified) {
      setApprovalWarningOrder({ order, newStatus });
      return;
    }
    
    // Otherwise proceed with status change
    updateOrderStatusMutation.mutate({ orderId: order.id, newStatus });
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, any> = {
      unapproved: 'secondary',
      pending: 'secondary',
      confirmed: 'default',
      in_production: 'default',
      shipped: 'default',
      delivered: 'default',
      cancelled: 'destructive',
    };
    
    return <Badge variant={variants[status] || 'outline'}>{status?.toUpperCase() || 'UNKNOWN'}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Orders
          </CardTitle>
          <CardDescription>
            Customer orders are automatically generated from invoices
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Order Date</TableHead>
              <TableHead>Expected Delivery</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Invoice Status</TableHead>
              <TableHead>Total Amount</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">Loading...</TableCell>
              </TableRow>
            ) : orders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No orders found. Create an invoice to automatically generate an order.
                </TableCell>
              </TableRow>
            ) : (
              orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="font-medium">{order.order_number}</TableCell>
                  <TableCell>{(order.customer as any)?.company_name}</TableCell>
                  <TableCell>
                    {format(new Date(order.order_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {order.expected_delivery_date 
                      ? format(new Date(order.expected_delivery_date), 'MMM dd, yyyy')
                      : 'TBD'
                    }
                  </TableCell>
                  <TableCell>{getStatusBadge(order.status)}</TableCell>
                  <TableCell>
                    {order.invoice_modified ? (
                      <div className="space-y-1">
                        <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                          <AlertTriangle className="h-3 w-3" />
                          INVOICE MODIFIED
                        </Badge>
                        {order.invoice_modified_at && (
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(order.invoice_modified_at), 'MMM d, h:mm a')}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell>${order.total_amount.toFixed(2)}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {order.status === 'unapproved' && (
                          <DropdownMenuItem onClick={() => handleStatusChange(order, 'confirmed')}>
                            <Package className="mr-2 h-4 w-4" />
                            Approve Order
                          </DropdownMenuItem>
                        )}
                        {order.status === 'pending' && (
                          <DropdownMenuItem onClick={() => updateOrderStatusMutation.mutate({ orderId: order.id, newStatus: 'confirmed' })}>
                            <Package className="mr-2 h-4 w-4" />
                            Confirm Order
                          </DropdownMenuItem>
                        )}
                        {order.status === 'confirmed' && (
                          <DropdownMenuItem onClick={() => updateOrderStatusMutation.mutate({ orderId: order.id, newStatus: 'in_production' })}>
                            <Package className="mr-2 h-4 w-4" />
                            Start Production
                          </DropdownMenuItem>
                        )}
                        {order.status === 'in_production' && (
                          <DropdownMenuItem onClick={() => updateOrderStatusMutation.mutate({ orderId: order.id, newStatus: 'shipped' })}>
                            <Truck className="mr-2 h-4 w-4" />
                            Mark as Shipped
                          </DropdownMenuItem>
                        )}
                        {order.status === 'shipped' && (
                          <DropdownMenuItem onClick={() => updateOrderStatusMutation.mutate({ orderId: order.id, newStatus: 'delivered' })}>
                            <Package className="mr-2 h-4 w-4" />
                            Mark as Delivered
                          </DropdownMenuItem>
                        )}
                        {order.status !== 'cancelled' && order.status !== 'delivered' && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                <Trash2 className="mr-2 h-4 w-4" />
                                Cancel Order
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Cancel Order</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to cancel this order? This will release all committed inventory.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction 
                                  onClick={() => updateOrderStatusMutation.mutate({ orderId: order.id, newStatus: 'cancelled' })}
                                >
                                  Yes, Cancel Order
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </CardContent>

      {/* Warning Dialog for Modified Invoice */}
      <AlertDialog open={!!approvalWarningOrder} onOpenChange={(open) => !open && setApprovalWarningOrder(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Invoice Has Been Modified
            </AlertDialogTitle>
            <AlertDialogDescription>
              The invoice linked to this order was modified on{' '}
              {approvalWarningOrder?.order.invoice_modified_at && 
                format(new Date(approvalWarningOrder.order.invoice_modified_at), 'MMM d, yyyy \'at\' h:mm a')
              }. Please review the invoice before approving this order.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (approvalWarningOrder) {
                  updateOrderStatusMutation.mutate({
                    orderId: approvalWarningOrder.order.id,
                    newStatus: approvalWarningOrder.newStatus,
                  });
                }
              }}
            >
              Approve Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
